import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Menu, X, Mail, ChevronRight } from "lucide-react";
import { brand, navLinks } from "../mock";
import { Button } from "./ui/button";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(to.slice(1));
        el && el.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      navigate(to);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md py-2" : "bg-white/95 backdrop-blur py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => setOpen(false)}>
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#e2001a] text-white font-display font-extrabold text-lg">
            A
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display font-extrabold text-lg tracking-tight text-[#16181d]">
              {brand.name}
            </span>
            <span className="text-[10px] font-medium text-gray-400 hidden sm:block">{brand.tagline}</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNav(l.to)}
              className="px-3 py-2 text-sm font-medium text-[#3a3f4a] hover:text-[#e2001a] transition-colors rounded-md"
            >
              {l.label}
            </button>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <a
            href={brand.phoneHref}
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#16181d] hover:text-[#e2001a] transition-colors"
          >
            <Phone className="w-4 h-4" /> {brand.phone}
          </a>
          <Button
            onClick={() => handleNav("/#contatti")}
            className="hidden sm:inline-flex bg-[#e2001a] hover:bg-[#c00016] text-white rounded-full px-5"
          >
            Prenota
          </Button>
          <button
            className="lg:hidden p-2 text-[#16181d]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white animate-fade-up">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            {navLinks.map((l) => (
              <button
                key={l.label}
                onClick={() => handleNav(l.to)}
                className="flex items-center justify-between py-3 text-[#16181d] font-medium border-b border-gray-50"
              >
                {l.label}
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            ))}
            <div className="flex gap-3 pt-4">
              <a href={brand.phoneHref} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 py-2.5 text-sm font-semibold">
                <Phone className="w-4 h-4" /> Chiama
              </a>
              <a href={`mailto:${brand.email}`} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#e2001a] text-white py-2.5 text-sm font-semibold">
                <Mail className="w-4 h-4" /> Scrivici
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
